/**
 * GrappleSkillEffect — Grit grapple-path close-range grab.
 *
 * Kinetic tendrils bind a single target in place for 1.5 seconds. Zero
 * damage — held targets take 1.5x from all other sources via the
 * `grapple_held` status. The player can move and use other skills during
 * the hold.
 *
 * Uses a custom state machine (idle -> windup -> active -> recovery ->
 * cooldown) rather than PhasedSkillEffect, because the hold phase
 * requires early cancellation (target death -> recovery -> cooldown)
 * and fizzle mechanics (skip active phase entirely) — both impossible
 * with PhasedSkillEffect's private phase internals.
 *
 * Targeting and fizzle determination are injected via deps.targeting
 * so the skill stays pure logic. The game layer provides the answers;
 * the skill acts on them.
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see grit-spec.md § Grapple
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface GrappleDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    hasEffect(skillId: string, key: string): boolean;
  };
  readonly statusEffects: {
    apply(statusId: string, ...args: unknown[]): void;
    remove(statusId: string, ...args: unknown[]): void;
    has(statusId: string, ...args: unknown[]): boolean;
  };
  readonly targeting: {
    hasTargetInRange(rangeTiles: number): boolean;
    shouldFizzle(): boolean;
    isTargetAlive(): boolean;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface GrappleConfig {
  readonly windupMs: number;
  readonly holdDurationMs: number;
  readonly recoveryMs: number;
  readonly cooldownMs: number;
  readonly baseRangeTiles: number;
  readonly baseHeldMultiplier: number;
  readonly damageReduction: number;
}

const DEFAULT_CONFIG: GrappleConfig = {
  windupMs: 100,
  holdDurationMs: 1500,
  recoveryMs: 200,
  cooldownMs: 1200,            // medium cooldown tier
  baseRangeTiles: 0.8,         // close melee range
  baseHeldMultiplier: 1.5,     // all incoming damage multiplied
  damageReduction: 0.4,        // Clinch passive: DR during hold
};

// ---------------------------------------------------------------------------
// Phase type
// ---------------------------------------------------------------------------

export type GrapplePhase = 'idle' | 'windup' | 'active' | 'recovery' | 'cooldown';

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class GrappleSkillEffect implements SkillEffect {
  readonly skillId = 'grapple';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;

  private readonly deps: GrappleDeps;
  private readonly config: GrappleConfig;

  private _phase: GrapplePhase = 'idle';
  private _phaseTimer = 0;
  private _complete = false;
  private _fizzled = false;

  private _resolvedRange = 0;
  private _resolvedMultiplier = 0;
  private _holdActive = false;
  private _rootApplied = false;
  private _heldApplied = false;
  private _drApplied = false;
  private _holdElapsedMs = 0;

  constructor(deps: GrappleDeps, config: Partial<GrappleConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get phase(): GrapplePhase { return this._phase; }
  get holdActive(): boolean { return this._holdActive; }
  get resolvedRange(): number { return this._resolvedRange; }
  get resolvedMultiplier(): number { return this._resolvedMultiplier; }
  get rootApplied(): boolean { return this._rootApplied; }
  get heldApplied(): boolean { return this._heldApplied; }
  get drApplied(): boolean { return this._drApplied; }
  get holdElapsedMs(): number { return this._holdElapsedMs; }
  get fizzled(): boolean { return this._fizzled; }

  get cooldown(): number {
    return this._phase === 'cooldown' ? this._phaseTimer : 0;
  }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    if (this._phase !== 'idle') return false;
    const bonusRange = this.deps.resolver.getAdditiveEffect('grapple', 'rangeTiles');
    this._resolvedRange = this.config.baseRangeTiles + bonusRange;
    return this.deps.targeting.hasTargetInRange(this._resolvedRange);
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    this._complete = false;
    this._holdActive = false;
    this._rootApplied = false;
    this._heldApplied = false;
    this._drApplied = false;
    this._holdElapsedMs = 0;
    this._resolvedMultiplier = 0;
    this._fizzled = this.deps.targeting.shouldFizzle();

    // Enter windup
    this._phase = 'windup';
    this._phaseTimer = this.config.windupMs;

    return true;
  }

  deactivate(): void {
    if (this._phase === 'active') {
      this.endHold();
    }
  }

  isActive(): boolean {
    return this._phase === 'windup' || this._phase === 'active' || this._phase === 'recovery';
  }

  isComplete(): boolean {
    return this._complete;
  }

  interrupt(): void {
    if (this._phase === 'idle' || this._phase === 'cooldown') return;
    this.removeDr();
    this._holdActive = false;
    this._phase = 'idle';
    this._phaseTimer = 0;
    this._complete = true;
  }

  update(delta: number): void {
    if (this._phase === 'idle') return;

    // During hold: track elapsed time and check target liveness
    if (this._phase === 'active') {
      this._holdElapsedMs += delta;
      if (!this.deps.targeting.isTargetAlive()) {
        this.endHold();
        return;
      }
    }

    this._phaseTimer -= delta;
    if (this._phaseTimer <= 0) {
      this.advancePhase();
    }
  }

  destroy(): void {
    this.removeDr();
    this._phase = 'idle';
    this._phaseTimer = 0;
    this._holdActive = false;
  }

  // --- Internal ---

  private advancePhase(): void {
    switch (this._phase) {
      case 'windup':
        if (this._fizzled) {
          this.enterRecovery();
        } else {
          this.enterHold();
        }
        break;
      case 'active':
        this.endHold();
        break;
      case 'recovery':
        this._phase = 'cooldown';
        this._phaseTimer = this.config.cooldownMs;
        this._complete = true;
        break;
      case 'cooldown':
        this._phase = 'idle';
        this._phaseTimer = 0;
        break;
    }
  }

  private enterHold(): void {
    this._phase = 'active';
    this._phaseTimer = this.config.holdDurationMs;
    this._holdActive = true;

    // Resolve held damage multiplier
    const bonusMultiplier = this.deps.resolver.getAdditiveEffect('grapple', 'heldDamageMultiplier');
    this._resolvedMultiplier = this.config.baseHeldMultiplier + bonusMultiplier;

    // Apply statuses to target
    this.deps.statusEffects.apply('root', this.config.holdDurationMs, 'grapple');
    this._rootApplied = true;

    this.deps.statusEffects.apply('grapple_held', this.config.holdDurationMs, this._resolvedMultiplier, 'grapple');
    this._heldApplied = true;

    // Clinch passive: DR during any grapple-path hold
    if (this.deps.resolver.hasEffect('grapple', 'grappleDamageReduction')) {
      this.deps.statusEffects.apply('grit_force_bind_dr', this.config.holdDurationMs, this.config.damageReduction, 'grapple');
      this._drApplied = true;
    }
  }

  private endHold(): void {
    this.removeDr();
    this._holdActive = false;
    this.enterRecovery();
  }

  private enterRecovery(): void {
    this._phase = 'recovery';
    this._phaseTimer = this.config.recoveryMs;
  }

  private removeDr(): void {
    if (this._drApplied) {
      this.deps.statusEffects.remove('grit_force_bind_dr', 'grapple');
      this._drApplied = false;
    }
  }
}
